import React, { useState, useEffect } from "react";
import { useLocation, Redirect } from "react-router-dom";
import { Layout, Drawer } from "antd";
import Sidenav from "./Sidenav";
import Header from "./Header";
import Footer from "./Footer";
import WorkStatusWidget from "./WorkStatusWidget";
import ActivityTracker from "./ActivityTracker";
import FloatingChat from "./FloatingChat";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { isDirectoryOnlyUser } from "../permissions";

const { Content, Sider } = Layout;

const HEADER_HEIGHT   = 64;
const SIDENAV_WIDTH   = 240;
const SIDENAV_COLLAPSED = 64;

// Groups that have submenus → show contextual sidenav
const SUBMENU_GROUPS = new Set([
  'reports',
  'administration',
  'community',
  'data-directory',
]);

// Map the current pathname to a top-nav group key
export function getActiveGroup(pathname) {
  const p = '/' + pathname.replace(/^\//, '');

  if (p === '/payments/add_user_payment') return 'add-payment';
  if (p.startsWith('/dashboard'))          return 'dashboard';
  if (p.startsWith('/users'))              return 'members';

  // Dashboard quick-access pages — keep header on Dashboard, no sidenav
  if (p.startsWith('/visitor'))     return 'dashboard';
  if (p.startsWith('/inquiry'))     return 'community';
  if (p.startsWith('/registration'))return 'dashboard';
  if (p.startsWith('/purnima'))     return 'dashboard';
  if (p.startsWith('/vishesh'))     return 'community';
  if (p.startsWith('/comp-exam'))   return 'community';

  // Financial routes now live under Administration sidenav
  if (
    p.startsWith('/expenditure-mgmt')  ||
    p.startsWith('/financial-reports') ||
    p.startsWith('/expenditures')      ||
    p.startsWith('/beneficiaries')     ||
    p.startsWith('/purposes')          ||
    p.startsWith('/budget-allocations') ||
    p.startsWith('/approval-workflow') ||
    p.startsWith('/reports/expenditure') ||
    p.startsWith('/reports/budget')    ||
    p.startsWith('/reports/approval')
  ) return 'administration';

  if (p.startsWith('/reports')) return 'reports';

  // /payments/* (except add_user_payment handled above)
  if (p.startsWith('/payments')) return 'transaction';

  if (p.startsWith('/village') && !p.startsWith('/pop-village')) return 'administration';

  if (p.startsWith('/festival-campaign')) return 'dashboard';
  if (p.startsWith('/poster'))            return 'dashboard';

  if (p.startsWith('/work')) return 'work-comm';
  // ── Community tab ──
  if (p.startsWith('/jobs-admin'))            return 'community';
  if (p.startsWith('/vivah-admin'))           return 'community';
  if (p.startsWith('/samaj-parichay'))        return 'community';
  if (p.startsWith('/sammelan'))              return 'community';
  if (p.startsWith('/gram-pramukh'))          return 'community';
  if (p.startsWith('/gotra-directory'))       return 'community';
  if (p.startsWith('/contributions'))         return 'community';
  if (p.startsWith('/festivals'))             return 'community';
  if (p.startsWith('/daily-quotes'))          return 'community';
  if (p.startsWith('/music-library'))         return 'community';
  if (p.startsWith('/samaj-properties'))      return 'community';
  if (p.startsWith('/admin-status'))          return 'community';
  if (p.startsWith('/student-support'))       return 'community';
  if (p.startsWith('/monthly-reports'))       return 'community';
  if (p.startsWith('/portal-updates'))        return 'community';
  if (p.startsWith('/portal-categories'))     return 'community';
  if (p.startsWith('/admin-notifications'))   return 'community';
  if (p.startsWith('/community-board'))       return 'community';

  // ── Manage tab ──
  if (p.startsWith('/message-log'))         return 'administration';
  if (p.startsWith('/receipt-template'))    return 'administration';
  if (p.startsWith('/bank-account'))        return 'administration';
  if (p.startsWith('/special-occasion'))    return 'administration';
  if (p.startsWith('/app-settings'))        return 'administration';

  if (p.startsWith('/announced')) return 'dashboard';

  if (p.startsWith('/pop-'))                return 'data-directory';
  if (p.startsWith('/business-directory')) return 'data-directory';
  if (p.startsWith('/directory-mobile'))   return 'data-directory';
  if (p.startsWith('/staff-chat'))          return 'staff-chat';
  if (p.startsWith('/staff-tracking')) return 'administration';

  if (p.startsWith('/trash')) return 'transaction';

  if (
    p.startsWith('/staff-role') ||
    p.startsWith('/role') ||
    p.startsWith('/staff') ||
    p.startsWith('/tracking')
  ) return 'administration';

  return 'dashboard';
}

function Main({ children }) {
  const history = useHistory();
  const { pathname } = useLocation();

  const [sidenavColor, setSidenavColor]   = useState('#1890ff');
  const [sidenavType, setSidenavType]     = useState('transparent');
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen]     = useState(false);
  const [collapsed, setCollapsed]               = useState(false);

  const activeGroup = getActiveGroup(pathname);
  const showSidenav = SUBMENU_GROUPS.has(activeGroup);

  useEffect(() => {
    if (!localStorage.getItem('accessToken')) {
      // On the public portal domain, redirect to the public form — not the admin sign-in
      if (window.location.hostname === 'portal.brahmdhamtirth.org') {
        history.replace('/business-directory-form');
      } else {
        history.push('/sign-in');
      }
    }
  }, [history]);

  // Directory-only staff → always redirect to mobile view (no sidebar/header)
  if (isDirectoryOnlyUser()) {
    return <Redirect to="/directory-mobile" />;
  }

  const toggleEditDrawer = (isOpen) => setEditDrawerOpen(isOpen);

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f6fa' }}>

      {/* ── Fixed Top Header ──────────────────────────────────── */}
      <div style={{
        position:  'fixed',
        top:       0,
        left:      0,
        right:     0,
        zIndex:    100,
        height:    HEADER_HEIGHT,
      }}>
        <Header
          activeGroup={activeGroup}
          onMobileMenuOpen={() => setMobileDrawerOpen(true)}
          handleSidenavColor={setSidenavColor}
          handleSidenavType={setSidenavType}
          handleFixedNavbar={() => {}}
        />
      </div>

      {/* ── Mobile Navigation Drawer ──────────────────────────── */}
      <Drawer
        title={<span style={{ fontWeight: 700, fontSize: 15 }}>Navigation</span>}
        placement="left"
        onClose={() => setMobileDrawerOpen(false)}
        open={mobileDrawerOpen}
        width={260}
        bodyStyle={{ padding: 0 }}
      >
        <Sidenav activeGroup={activeGroup} color={sidenavColor} showAll />
      </Drawer>

      {/* ── Body (below fixed header) ─────────────────────────── */}
      <Layout style={{ marginTop: HEADER_HEIGHT }}>

        {/* Conditional contextual sidenav */}
        {showSidenav && (
          <Sider
            width={collapsed ? SIDENAV_COLLAPSED : SIDENAV_WIDTH}
            collapsedWidth={SIDENAV_COLLAPSED}
            collapsed={collapsed}
            theme="light"
            style={{
              position:    'fixed',
              top:         HEADER_HEIGHT,
              left:        0,
              height:      `calc(100vh - ${HEADER_HEIGHT}px)`,
              overflowY:   'auto',
              overflowX:   'hidden',
              background:  '#7a0000',
              borderRight: '1px solid rgba(255,255,255,0.08)',
              zIndex:      90,
              transition:  'width 0.25s ease',
            }}
          >
            {/* Toggle button */}
            <div
              onClick={() => setCollapsed(c => !c)}
              style={{
                display:        'flex',
                alignItems:     'center',
                justifyContent: collapsed ? 'center' : 'flex-end',
                padding:        '10px 12px',
                cursor:         'pointer',
                borderBottom:   '1px solid rgba(255,255,255,0.08)',
                userSelect:     'none',
              }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'rgba(255,255,255,0.08)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 16, transition: 'all 0.2s',
              }}>
                {collapsed ? '›' : '‹'}
              </div>
            </div>

            <Sidenav activeGroup={activeGroup} color={sidenavColor} collapsed={collapsed} />
          </Sider>
        )}

        {/* Main content */}
        <Content
          className="content-ant"
          style={{
            marginLeft:    showSidenav ? (collapsed ? SIDENAV_COLLAPSED : SIDENAV_WIDTH) : 0,
            padding:       '16px',
            minHeight:     `calc(100vh - ${HEADER_HEIGHT}px)`,
            opacity:       editDrawerOpen ? 0.3 : 1,
            pointerEvents: editDrawerOpen ? 'none' : 'auto',
            transition:    'margin-left 0.25s ease',
          }}
        >
          {React.cloneElement(children, { toggleEditDrawer })}
          <Footer />
        </Content>

      </Layout>

      {/* Floating work status widget — visible on every page for staff */}
      <WorkStatusWidget />

      {/* Global activity tracker — idle detection + auto clock-out */}
      <ActivityTracker />

      {/* Floating chat button — visible on every page */}
      <FloatingChat />

    </Layout>
  );
}

export default Main;
